import React from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  size?: ModalSize;
  /** If false, clicking the backdrop does not close the modal. */
  closeOnOverlayClick?: boolean;
  /** If false, Escape key does not close the modal. */
  closeOnEsc?: boolean;
  /** Hides the built-in "×" close button. */
  hideCloseButton?: boolean;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Accessibility label when no title is provided. */
  'aria-label'?: string;
}

let nextModalId = 1;

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  hideCloseButton,
  children,
  footer,
  'aria-label': ariaLabel
}) => {
  const titleId = React.useMemo(() => `bso-modal-title-${nextModalId++}`, []);
  const descId = React.useMemo(() => `bso-modal-desc-${nextModalId++}`, []);
  const modalRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose, closeOnEsc]);

  if (!open || typeof document === 'undefined') return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!closeOnOverlayClick) return;
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div className="bso-modal-overlay" onMouseDown={handleOverlayClick}>
      <div
        ref={modalRef}
        className={`bso-modal bso-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        tabIndex={-1}
      >
        {(title || !hideCloseButton) && (
          <header className="bso-modal__header">
            <div>
              {title && (
                <h2 id={titleId} className="bso-modal__title">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="bso-modal__description">
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                className="bso-modal__close"
                aria-label="Close"
                onClick={onClose}
              >
                ×
              </button>
            )}
          </header>
        )}
        <div className="bso-modal__body">{children}</div>
        {footer && <footer className="bso-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
};
